/**
 * One-shot screenshot capture for the feedback widget.
 *
 * User-initiated only (button click → getDisplayMedia prompt). We grab a SINGLE
 * frame, stop the track immediately (no ongoing capture), downscale to keep the
 * payload well under the backend's 4 MiB screenshot / 5 MiB request caps, and
 * encode as JPEG. Returns null on denial / unsupported — callers degrade
 * gracefully (feedback stays submittable without a screenshot).
 */
const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.7;

export interface CaptureResult {
  /** `data:image/jpeg;base64,...` URI. */
  dataUri: string;
  /** Approx decoded byte size of the base64 payload (for the size budget). */
  bytes: number;
  width: number;
  height: number;
}

/** Approx byte size of the base64 portion of a data URI (~3/4 of its char length). */
export function dataUriBytes(dataUri: string): number {
  const comma = dataUri.indexOf(",");
  const b64 = comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
  return Math.round((b64.length * 3) / 4);
}

export function screenshotSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;
}

export async function captureScreenshot(): Promise<CaptureResult | null> {
  if (!screenshotSupported()) return null;

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      // preferCurrentTab is Chrome-only and not in the lib DOM types yet.
      video: { preferCurrentTab: true } as MediaTrackConstraints,
      audio: false,
    });

    const video = document.createElement("video");
    video.muted = true;
    video.srcObject = stream;
    await video.play();
    // Let one frame land before drawing.
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    const vw = video.videoWidth || MAX_WIDTH;
    const vh = video.videoHeight || Math.round(MAX_WIDTH * 0.6);
    const scale = vw > MAX_WIDTH ? MAX_WIDTH / vw : 1;
    const w = Math.round(vw * scale);
    const h = Math.round(vh * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, w, h);

    const dataUri = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    return { dataUri, bytes: dataUriBytes(dataUri), width: w, height: h };
  } catch {
    // Permission denied / cancelled / unsupported — degrade silently.
    return null;
  } finally {
    // Stop capture immediately, no matter what.
    stream?.getTracks().forEach((t) => t.stop());
  }
}
